import action from "../helpers/rest-client";
import { SignUpData } from "../types/auth.dto";

export class ErrorAuth extends Error {}

class Auth {
  public getToken() {
    return localStorage.getItem("token");
  }

  public async refreshToken() {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    return action
      .Get({
        url: "/token",
      })
      .then((resp) => {
        const { token, user } = resp;
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        return resp.user;
      })
      .catch((error) => {
        throw new ErrorAuth(error.message);
      });
  }

  public getTokenExpirationTime() {
    const token = localStorage.getItem("token");

    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return new Date(payload.exp * 1000);
    }
  }

  public getTokenRemainingTime() {
    const tokenExpirationTime = this.getTokenExpirationTime();

    if (tokenExpirationTime) {
      const now = new Date();
      return tokenExpirationTime.getTime() - now.getTime();
    }

    return 0;
  }

  public async userAuthenticated(callback) {
    const tokenRemainingTime = this.getTokenRemainingTime();
    let isAutenticated = false;

    if (tokenRemainingTime) {
      const fiveMinutes = 1000 * 60 * 5;
      isAutenticated = tokenRemainingTime > fiveMinutes;
    }

    if (callback) {
      callback(isAutenticated);
    }
  }

  public async signUp(payload: SignUpData) {
    return action
      .Post({
        url: "/register",
        body: payload,
      })
      .then((resp) => {
        return resp;
      })
      .catch((error) => {
        throw new ErrorAuth(error.message);
      });
  }

  public async signIn(payload: any) {
    // Clean up payload - remove undefined and empty string values
    const cleanPayload = Object.keys(payload).reduce((acc, key) => {
      if (payload[key] !== undefined && payload[key] !== "") {
        acc[key] = payload[key];
      }
      return acc;
    }, {} as any);

    return action
      .Post({
        url: "/login",
        body: cleanPayload,
      })
      .then((resp) => {
        // Check for 202 response first (can be in error format)
        if (resp.error && resp.error.message && resp.error.message.includes("autenticación 2FA")) {
          return {
            user: null,
            token: null,
            browserHash: null,
            httpCode: 202,
            message: resp.error.message || "Configure su autenticación 2FA para ingresar",
          };
        }
        
        const { token, user, browser_hash } = resp.success?.result || {};
        const httpCode = resp.httpCode || resp.status;
        
        // Handle 202 response (backup code used, MFA temporarily disabled)
        if (httpCode === 202) {
          return {
            user: null,
            token: null,
            browserHash: null,
            httpCode: 202,
            message: resp.message || "Código de respaldo verificado",
          };
        }
        
        if (token && user) {
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));
          
          // Store browser hash if provided
          if (browser_hash) {
            localStorage.setItem("browserHash", browser_hash);
          }
        }
        
        return {
          user,
          token,
          browserHash: browser_hash,
          httpCode: httpCode,
          message: resp.message,
        };
      })
      .catch((error) => {
        // Extract error information from axios error structure
        const httpCode = error.response?.status || error.httpCode || error.status || 401;
        const errorData = error.response?.data || {};
        const message = errorData.error?.message || error.message || "Error en el inicio de sesión";
        
        // Return error details for handling MFA, lockout, etc.
        throw {
          httpCode,
          message,
          qrCodeUrl: errorData.error?.qrCodeUrl,
          secret: errorData.error?.secret,
        };
      });
  }

  public signOut() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Only remove browserHash if device wasn't set to be remembered
    const rememberDevice = localStorage.getItem("rememberDevice");
    if (rememberDevice !== "true") {
      localStorage.removeItem("browserHash");
    }
  }

  public recoverPassword(payload) {
    return action
      .Post({
        url: "/forgot",
        body: payload,
      })
      .then((resp) => {
        return resp.success;
      })
      .catch((error) => {
        // Extract error information from axios error structure
        const httpCode = error.response?.status || error.httpCode || error.status || 500;
        const errorData = error.response?.data || {};
        const message = errorData.error?.message || error.message || "Error al recuperar contraseña";
        
        throw {
          httpCode,
          message,
        };
      });
  }

  public resetPassword(payload) {
    return action
      .Post({
        url: "/reset",
        body: payload,
      })
      .then((resp) => {
        return resp.user;
      })
      .catch((error) => {
        // Extract error information from axios error structure
        const httpCode = error.response?.status || 500;
        const errorData = error.response?.data || {};
        const message = errorData.error?.message || error.message || "Error al restablecer contraseña";
        
        throw {
          httpCode,
          message,
        };
      });
  }
}

const auth = new Auth();
export default auth;
